import { BadgeCheck, Cake, Camera, Check, Globe, Loader2, Mail, MapPin, Phone, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from '../../api/client.ts';
import { useProfile, useSaveProfile } from '../../api/queries.ts';
import type { Profile } from '../../api/types.ts';
import { SampleDataNote } from '../../components/layout/SampleDataNote.tsx';
import { initials } from '../../components/layout/TopBar.tsx';
import { Button } from '../../components/ui/button.tsx';
import { ErrorNote, Loading, PageTitle, errorMessage } from '../../components/ui/feedback.tsx';
import { TextAreaField, TextField } from '../../components/ui/field.tsx';
import { cn } from '../../lib/cn.ts';
import { buttonTapped } from '../../lib/usage.ts';
import { pickImage } from '../signature/pickImage.ts';
import {
  TEXT_LIMITS,
  changesFrom,
  draftOf,
  normaliseWebsite,
  roleLine,
  validate,
  type ProfileDraft,
  type ProfileErrors,
} from './profileForm.ts';

export function ProfilePage() {
  const profile = useProfile();
  return (
    <>
      <PageTitle
        description="How you appear to the people you email, in the app on every device you use."
        aside={profile.data?.source === 'sample' ? <SampleDataNote /> : null}
      >
        Profile
      </PageTitle>
      {profile.isPending ? <Loading /> : null}
      {profile.isError ? <ErrorNote title="Could not load your profile">{errorMessage(profile.error)}</ErrorNote> : null}
      {profile.data ? <ProfileForm profile={profile.data} /> : null}
    </>
  );
}

/** Field errors the server sent back with a 400, by field. */
function serverErrors(error: unknown): ProfileErrors {
  if (!(error instanceof ApiError) || error.status !== 400) return {};
  const issues = (error.body as { issues?: Record<string, string[] | undefined> } | undefined)?.issues ?? {};
  const out: ProfileErrors = {};
  for (const [key, messages] of Object.entries(issues)) {
    if (messages?.[0]) (out as Record<string, string>)[key] = messages[0];
  }
  return out;
}

function ProfileForm({ profile }: { profile: Profile }) {
  const save = useSaveProfile();
  const base = useMemo(() => draftOf(profile), [profile]);
  const [draft, setDraft] = useState<ProfileDraft>(base);
  const [touched, setTouched] = useState<Partial<Record<keyof ProfileDraft, boolean>>>({});
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // A fresh profile from the server (after a save) becomes the new baseline.
  useEffect(() => setDraft(base), [base]);
  // "Profile saved" is a confirmation, not a state: it goes after a moment.
  useEffect(() => {
    if (!savedAt) return;
    const timer = setTimeout(() => setSavedAt(null), 2500);
    return () => clearTimeout(timer);
  }, [savedAt]);

  const changes = changesFrom(base, draft);
  const dirty = Object.keys(changes).length > 0;
  const clientErrors = validate(draft);
  const errors: ProfileErrors = { ...serverErrors(save.error), ...clientErrors };
  const shownError = (key: keyof ProfileDraft) => (touched[key] || save.isError ? errors[key] : undefined);

  const set = <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setSavedAt(null);
  };
  const text = (key: keyof typeof TEXT_LIMITS) => ({
    value: draft[key],
    onChange: (event: { target: { value: string } }) => set(key, event.target.value),
    onBlur: () => setTouched((current) => ({ ...current, [key]: true })),
  });

  const choosePhoto = async () => {
    setPhotoError(null);
    try {
      const picked = await pickImage('avatar');
      if (picked) set('photo', picked);
    } catch (error) {
      setPhotoError(error instanceof Error ? error.message : String(error));
    }
  };

  const submit = () => {
    setTouched(Object.fromEntries(Object.keys(draft).map((key) => [key, true])));
    if (Object.keys(clientErrors).length) return;
    save.mutate(changes, { onSuccess: () => setSavedAt(Date.now()) });
  };

  const discard = () => {
    setDraft(base);
    setTouched({});
    save.reset();
  };

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      className="grid gap-x-12 gap-y-10 lg:grid-cols-[minmax(0,1fr)_19rem]"
    >
      <div className="flex min-w-0 flex-col gap-10">
        <PhotoRow
          name={draft.name}
          email={profile.email}
          photo={draft.photo}
          verified={profile.verified}
          onChoose={profile.photoUpload ? choosePhoto : undefined}
          onRemove={() => set('photo', null)}
          error={photoError}
        />

        <Section title="Name and role">
          <TextField label="Full name" autoComplete="name" error={shownError('name')} {...text('name')} />
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Job title" autoComplete="organization-title" placeholder="What you do" error={shownError('jobTitle')} {...text('jobTitle')} />
            <TextField label="Company" autoComplete="organization" placeholder="Where you work" error={shownError('company')} {...text('company')} />
          </div>
        </Section>

        <Section title="How to reach you" note="Your email address is always shown. Add the rest only if you want people to see it.">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField label="Phone" type="tel" autoComplete="tel" inputMode="tel" error={shownError('phone')} {...text('phone')} />
            <TextField
              label="Website"
              type="url"
              autoComplete="url"
              placeholder="example.com"
              error={shownError('website')}
              {...text('website')}
              onBlur={() => {
                set('website', normaliseWebsite(draft.website));
                setTouched((current) => ({ ...current, website: true }));
              }}
            />
          </div>
          <TextField label="Location" autoComplete="address-level2" placeholder="City, country" error={shownError('location')} {...text('location')} />
        </Section>

        <Section title="About you">
          <TextAreaField
            label="Bio"
            placeholder="A line or two about you"
            error={shownError('bio')}
            count={{ used: draft.bio.trim().length, limit: TEXT_LIMITS.bio }}
            {...text('bio')}
          />
          <TextField
            label="Birthday"
            type="date"
            className="sm:max-w-56"
            max={new Date().toISOString().slice(0, 10)}
            error={shownError('birthday')}
            value={draft.birthday}
            onChange={(event) => set('birthday', event.target.value)}
            onBlur={() => setTouched((current) => ({ ...current, birthday: true }))}
          />
        </Section>

        {save.isError && !Object.keys(serverErrors(save.error)).length ? (
          <ErrorNote title="Your profile was not saved">{errorMessage(save.error)}</ErrorNote>
        ) : null}
      </div>

      <aside className="lg:sticky lg:top-0 lg:self-start">
        <p className="mb-3 text-sm font-semibold text-muted">How contacts see you</p>
        <ContactCard draft={draft} email={profile.email} verified={profile.verified} />
      </aside>

      <SaveBar dirty={dirty} saving={save.isPending} savedAt={savedAt} onDiscard={discard} />
    </form>
  );
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-5">
      <div>
        <h2 className="font-display text-xl font-semibold text-ink">{title}</h2>
        {note ? <p className="mt-1 max-w-[60ch] text-sm text-muted">{note}</p> : null}
      </div>
      {children}
    </section>
  );
}

function Avatar({ name, email, photo, size }: { name: string; email: string; photo: string | null; size: 'lg' | 'md' }) {
  const box = size === 'lg' ? 'h-24 w-24 text-3xl' : 'h-20 w-20 text-2xl';
  if (photo) return <img src={photo} alt="" className={cn(box, 'shrink-0 rounded-full object-cover')} />;
  return (
    <span aria-hidden className={cn(box, 'flex shrink-0 items-center justify-center rounded-full bg-brand-soft font-display font-semibold text-brand')}>
      {initials({ name, email })}
    </span>
  );
}

const VERIFIED: Record<Profile['verified'], { label: string; tone: string }> = {
  0: { label: 'Verification in progress', tone: 'text-muted' },
  1: { label: 'Verified address', tone: 'text-brand' },
  2: { label: 'Verified domain', tone: 'text-[#b7791f]' },
};

function VerifiedMark({ verified, withLabel }: { verified: Profile['verified']; withLabel?: boolean }) {
  const { label, tone } = VERIFIED[verified];
  if (verified === 0 && !withLabel) return null;
  return (
    <span className={cn('inline-flex items-center gap-1 text-sm font-semibold', tone)} title={label}>
      <BadgeCheck className="h-[18px] w-[18px] shrink-0" aria-hidden />
      {withLabel ? label : <span className="sr-only">{label}</span>}
    </span>
  );
}

function PhotoRow(props: {
  name: string;
  email: string;
  photo: string | null;
  verified: Profile['verified'];
  /** Absent while the web cannot upload a photo; removing one still works. */
  onChoose?: () => void;
  onRemove: () => void;
  error: string | null;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-5">
        <div className="group relative">
          <Avatar name={props.name} email={props.email} photo={props.photo} size="lg" />
          {props.onChoose ? (
            <button
              type="button"
              onClick={() => {
                buttonTapped('photo_choose');
                props.onChoose?.();
              }}
              className="absolute -right-1 -bottom-1 flex h-9 w-9 items-center justify-center rounded-full bg-ink text-surface shadow-[0_0_0_3px_var(--surface)] transition-transform hover:scale-105"
              aria-label={props.photo ? 'Change photo' : 'Add a photo'}
            >
              <Camera className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-2xl font-semibold text-ink">{props.name || 'Your name'}</p>
          <p className="truncate text-[0.95rem] text-muted">{props.email}</p>
          <div className="mt-1.5">
            <VerifiedMark verified={props.verified} withLabel />
          </div>
        </div>
        <div className="flex w-full gap-2 sm:w-auto">
          {props.onChoose ? (
            <Button type="button" variant="outline" size="sm" onClick={props.onChoose} track="photo_choose">
              {props.photo ? 'Change photo' : 'Add a photo'}
            </Button>
          ) : null}
          {props.photo ? (
            <Button type="button" variant="ghost" size="sm" className="text-danger hover:bg-danger-soft" onClick={props.onRemove} track="photo_remove">
              <Trash2 className="h-4 w-4" aria-hidden /> Remove
            </Button>
          ) : null}
        </div>
      </div>
      {props.error ? <p className="text-sm font-medium text-danger">Could not use that picture: {props.error}</p> : null}
      {props.onChoose ? null : <p className="text-sm text-muted">To change your photo, use the app on your phone or computer.</p>}
    </div>
  );
}

function formatBirthday(value: string): string {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
}

/** The contact card the apps draw for this account, filled from the draft as it is typed. */
function ContactCard({ draft, email, verified }: { draft: ProfileDraft; email: string; verified: Profile['verified'] }) {
  const role = roleLine(draft.jobTitle, draft.company);
  const rows: Array<{ icon: typeof Mail; text: string; href?: string }> = [
    { icon: Mail, text: email, href: `mailto:${email}` },
    ...(draft.phone.trim() ? [{ icon: Phone, text: draft.phone.trim(), href: `tel:${draft.phone.replace(/\s+/g, '')}` }] : []),
    ...(draft.website.trim()
      ? [{ icon: Globe, text: draft.website.trim().replace(/^https?:\/\//i, '').replace(/\/$/, ''), href: normaliseWebsite(draft.website) }]
      : []),
    ...(draft.location.trim() ? [{ icon: MapPin, text: draft.location.trim() }] : []),
    ...(draft.birthday ? [{ icon: Cake, text: formatBirthday(draft.birthday) }] : []),
  ];
  return (
    <div className="overflow-hidden rounded-[1.5rem] bg-surface shadow-[0_18px_40px_-24px_rgba(15,27,45,0.35),0_0_0_1px_var(--line)]">
      <div className="h-16 bg-[color-mix(in_oklab,var(--brand)_16%,var(--surface))]" aria-hidden />
      <div className="-mt-10 flex flex-col items-center px-6 pb-6 text-center">
        <div className="rounded-full shadow-[0_0_0_4px_var(--surface)]">
          <Avatar name={draft.name} email={email} photo={draft.photo} size="md" />
        </div>
        <p className="mt-3 flex items-center gap-1.5 font-display text-xl font-semibold text-ink">
          <span className="truncate">{draft.name.trim() || 'Your name'}</span>
          <VerifiedMark verified={verified} />
        </p>
        {role ? <p className="text-sm text-muted">{role}</p> : null}
        {draft.bio.trim() ? <p className="mt-3 text-sm leading-relaxed text-ink/85">{draft.bio.trim()}</p> : null}
      </div>
      <ul className="divide-y divide-line border-t border-line text-sm">
        {rows.map(({ icon: Icon, text, href }) => (
          <li key={text} className="flex items-center gap-3 px-5 py-3">
            <Icon className="h-4 w-4 shrink-0 text-muted" aria-hidden />
            {href ? (
              <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" className="truncate text-ink hover:text-brand">
                {text}
              </a>
            ) : (
              <span className="truncate text-ink">{text}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Floats over the bottom of the page while there is something to save. */
function SaveBar({ dirty, saving, savedAt, onDiscard }: { dirty: boolean; saving: boolean; savedAt: number | null; onDiscard: () => void }) {
  if (!dirty && !savedAt) return null;
  return (
    <div className="pointer-events-none sticky bottom-[calc(5.25rem+env(safe-area-inset-bottom))] z-30 flex justify-center md:bottom-6 lg:col-span-2">
      <div
        role="status"
        className="page-in pointer-events-auto flex items-center gap-2 rounded-full bg-ink py-2 pr-2 pl-5 text-sm text-surface shadow-[0_18px_40px_-14px_rgba(15,27,45,0.55)]"
      >
        {dirty ? (
          <>
            <span className="mr-2 font-medium">You have unsaved changes</span>
            <Button type="button" variant="ghost" size="sm" className="text-surface hover:bg-white/10" onClick={onDiscard} disabled={saving} track="profile_discard">
              Discard
            </Button>
            <Button type="submit" size="sm" disabled={saving} track="profile_save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        ) : (
          <span className="flex items-center gap-2 py-1.5 pr-3 font-medium">
            <Check className="h-4 w-4 text-ok" aria-hidden /> Profile saved
          </span>
        )}
      </div>
    </div>
  );
}
